import React, { useEffect, useState } from 'react';
import './LastUpdatedIndicator.css';

const API = process.env.REACT_APP_BACKEND_URL;

// Format an ISO timestamp as relative time (e.g. "2 hours ago").
function formatRelative(iso) {
  try {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return null;
    const diffMs = Date.now() - then;
    const sec = Math.max(0, Math.floor(diffMs / 1000));
    if (sec < 60) return 'just now';
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
    const day = Math.floor(hr / 24);
    if (day < 7) return `${day} day${day === 1 ? '' : 's'} ago`;
    const wk = Math.floor(day / 7);
    if (wk < 5) return `${wk} week${wk === 1 ? '' : 's'} ago`;
    const mo = Math.floor(day / 30);
    return `${mo} month${mo === 1 ? '' : 's'} ago`;
  } catch {
    return null;
  }
}

function formatAbsolute(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return iso;
  }
}

/**
 * Tiny "Last updated" indicator — signals to users (especially returning
 * subscribers) that the game is actively maintained.
 *
 * Shows relative time ("updated 2 hours ago") with the full timestamp as a
 * hover tooltip. Fetches /api/version once on mount; fails silently.
 */
const LastUpdatedIndicator = () => {
  const [version, setVersion] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/version`, { cache: 'no-store' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!cancelled && data?.version) setVersion(data.version);
      })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  // Re-render every 60s so "X minutes ago" stays fresh
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  if (!version) return null;

  const relative = formatRelative(version);
  const absolute = formatAbsolute(version);

  return (
    <div className="last-updated-indicator" data-testid="last-updated-indicator" data-tick={tick}>
      <span className="lu-dot" aria-hidden="true" />
      <span className="lu-label">Updated</span>
      <span className="lu-time" title={absolute} data-testid="last-updated-time">
        {relative}
      </span>
    </div>
  );
};

export default LastUpdatedIndicator;
